import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FileText, Receipt, Search, Table2, User } from 'lucide-react'
import { api } from '../lib/api'
import { navigeer } from '../lib/router'
import { bvKort, useGekozenBvs, zet } from '../lib/staat'
import { bedrag, waardeTekst } from '../lib/velden'
import { verkennerAdres } from '../lib/zoekstaat'
import type { ArchiefRij } from '../../gedeeld/types'
import { Laden } from './ui'

/* ===========================================================================
 *  Overal zoeken (Ctrl K)
 *
 *  Eén veld, vier plekken tegelijk: boekingsregels (op boekstuk en
 *  omschrijving), inkoop- en verkoopboekingen, en relaties. Pijltjes en
 *  Enter, zoals in het dashboard.
 * =========================================================================== */

interface Treffer { soort: 'boeking' | 'inkoop' | 'verkoop' | 'relatie' | 'alles'; titel: string; sub: string; bedrag?: number | null; naar: string }

export default function Zoeken() {
  const bvs = useGekozenBvs()
  const [tekst, setTekst] = useState('')
  const [treffers, setTreffers] = useState<Treffer[]>([])
  const [laden, setLaden] = useState(false)
  const [plek, setPlek] = useState(0)
  const veld = useRef<HTMLInputElement>(null)

  useEffect(() => { veld.current?.focus() }, [])
  useEffect(() => {
    const toets = (e: KeyboardEvent) => { if (e.key === 'Escape') zet({ zoekOpen: false }) }
    window.addEventListener('keydown', toets)
    return () => window.removeEventListener('keydown', toets)
  }, [])

  useEffect(() => {
    const t = tekst.trim()
    if (t.length < 2) { setTreffers([]); return }
    const stop = new AbortController()
    const wacht = setTimeout(() => {
      setLaden(true)
      const vraag = (soort: string) => api.lijst({ soort, divisies: bvs, tekst: t, voorwaarden: [] }, { perPagina: 6 }, stop.signal)
        .then((u) => u.rijen).catch(() => [] as ArchiefRij[])
      Promise.all([vraag('boekingen'), vraag('inkoopboekingen'), vraag('verkoopboekingen'), api.relaties(bvs, t).catch(() => [] as ArchiefRij[])])
        .then(([b, i, v, r]) => {
          const uit: Treffer[] = []
          const gezien = new Set<string>()
          for (const x of b) {
            const entry = String(x.rij.EntryID ?? '')
            if (gezien.has(entry)) continue
            gezien.add(entry)
            uit.push({ soort: 'boeking', titel: `${x.nummer} · ${x.omschrijving || String(x.rij.GLAccountDescription ?? '')}`, sub: `${bvKort(x.division)} · ${waardeTekst('@datum', x.datum)} · ${String(x.rij.JournalDescription ?? '')}`, bedrag: x.bedrag, naar: entry ? `/boeking/${x.division}/${entry}` : verkennerAdres({ soort: 'boekingen', tekst: t }) })
          }
          for (const x of i) uit.push({ soort: 'inkoop', titel: `${String(x.rij.SupplierName ?? '')} · ${String(x.rij.InvoiceNumber ?? x.nummer)}`, sub: `${bvKort(x.division)} · ${waardeTekst('@datum', x.datum)} · inkoop`, bedrag: x.bedrag, naar: `/boeking/${x.division}/${x.exactId}` })
          for (const x of v) uit.push({ soort: 'verkoop', titel: `${String(x.rij.CustomerName ?? '')} · ${String(x.rij.InvoiceNumber ?? x.nummer)}`, sub: `${bvKort(x.division)} · ${waardeTekst('@datum', x.datum)} · verkoop`, bedrag: x.bedrag, naar: `/boeking/${x.division}/${x.exactId}` })
          for (const x of r.slice(0, 8)) uit.push({ soort: 'relatie', titel: x.omschrijving, sub: `${bvKort(x.division)} · ${String(x.nummer).trim()} · ${String(x.rij.City ?? '')}`, naar: `/relatie/${x.division}/${encodeURIComponent(String(x.nummer).trim())}` })
          uit.push({ soort: 'alles', titel: `Alle boekingsregels met “${t}”`, sub: 'in de verkenner', naar: verkennerAdres({ soort: 'boekingen', tekst: t }) })
          setTreffers(uit)
          setPlek(0)
        })
        .finally(() => setLaden(false))
    }, 300)
    return () => { clearTimeout(wacht); stop.abort() }
  }, [tekst, bvs.join(',')]) // eslint-disable-line react-hooks/exhaustive-deps

  function ga(t: Treffer | undefined) {
    if (!t) return
    zet({ zoekOpen: false })
    navigeer(t.naar)
  }

  const ICOON = { boeking: <Receipt size={15} />, inkoop: <FileText size={15} />, verkoop: <FileText size={15} />, relatie: <User size={15} />, alles: <Table2 size={15} /> }

  return createPortal(
    <div className="scherm zoek-scherm" onMouseDown={(e) => { if (e.target === e.currentTarget) zet({ zoekOpen: false }) }}>
      <div className="zoek-venster" role="dialog" aria-modal>
        <div className="zoek-invoer">
          <Search size={17} />
          <input ref={veld} value={tekst} onChange={(e) => setTekst(e.target.value)} placeholder="Boekstuk, factuurnummer, omschrijving of relatie…"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setPlek((p) => Math.min(p + 1, treffers.length - 1)) }
              if (e.key === 'ArrowUp') { e.preventDefault(); setPlek((p) => Math.max(p - 1, 0)) }
              if (e.key === 'Enter') ga(treffers[plek])
            }} />
          <kbd>Esc</kbd>
        </div>
        <div className="zoek-uitkomst">
          {laden && !treffers.length && <Laden />}
          {!laden && tekst.trim().length >= 2 && treffers.length <= 1 && <div className="menu-leeg">Niets gevonden in {bvs.length === 1 ? bvKort(bvs[0]) : `${bvs.length} bv's`}.</div>}
          {treffers.map((t, i) => (
            <button key={i} type="button" className={`zoek-treffer ${i === plek ? 'actief' : ''}`} onMouseEnter={() => setPlek(i)} onClick={() => ga(t)}>
              <span className="zoek-icoon">{ICOON[t.soort]}</span>
              <span className="vul afkap"><strong className="afkap">{t.titel}</strong><span className="stil afkap">{t.sub}</span></span>
              {t.bedrag !== undefined && t.bedrag !== null && <span className={`bedrag ${t.bedrag < 0 ? 'neg' : ''}`}>{bedrag(t.bedrag)}</span>}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
